import { Pow18 } from 'config'
import { useAtom, useAtomValue } from 'jotai'
import { memo, useMemo } from 'react'
import { w2e, bn, e2w } from 'utils/number'
import {
  contractInformationAtom,
  contractInformationSymbolAtom,
} from '../Information/atom'
import { Stack, styled } from '@mui/material'
import numbro from 'numbro'
import dayjs from 'dayjs'
import { TooltipText } from 'components/TooltipText'
import { Link } from 'react-router-dom'
import { COMMUNITY_LINKS } from 'constants/community'

const Wrap = styled(Stack)`
  padding: 8px 24px 24px;
`
const Title = styled('h2')`
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  margin: 0;
`
const Body = styled('div')`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;
  padding: 16px 0;
`
const Column = styled(Stack)`
  gap: 12px;
`
const Row = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  line-height: 20px;

  span {
    font-size: 12px;
    color: rgba(149, 164, 173, 1);
  }
  b {
    font-weight: 400;
    font-size: 14px;
    color: rgba(229, 234, 238, 1);
    span {
      color: rgba(229, 234, 238, 1);
    }
  }
  a {
    color: rgba(160, 238, 9, 1);
  }
`
export const ContractInfoView = memo(() => {
  const [information] = useAtom(contractInformationAtom)

  return (
    <Wrap>
      <Title>
        {information?.baseCurrency}
        {information?.quoteCurrency} Perpetual
      </Title>

      <Body>
        <Column>
          <Row>
            <span>Initial Margin</span>
            <b>
              {numbro(information?.riskLimit?.initialMarginRate).format({
                output: 'percent',
                mantissa: 2,
              })}{' '}
              + Entry Taker Fee + Exit Taker Fee
            </b>
          </Row>
          <Row>
            <span>Maint Margin</span>
            <b>
              {numbro(information?.riskLimit?.maintenanceMarginRate).format({
                output: 'percent',
                mantissa: 2,
              })}{' '}
              + Exit Taker Fee + Funding Rate
            </b>
          </Row>
          <Row>
            <span>Funding Rate</span>
            <b>
              {numbro(information?.currentFundingRate).format({
                output: 'percent',
                mantissa: 4,
              })}
            </b>
          </Row>
          <Row>
            <span>Funding Interval</span>
            <b>every {information?.fundingInterval} hours</b>
          </Row>
          <Row>
            <span>Next Funding</span>
            <b>{dayjs(information?.nextFundingTime).toString()}</b>
          </Row>
          <Row>
            <span>Predicted Rate</span>
            <b>
              {numbro(information?.nextFundingRate).format({
                output: 'percent',
                mantissa: 4,
              })}
            </b>
          </Row>
          <Row>
            <span>Mark Price</span>
            <b>{information?.markPrice}</b>
          </Row>
          <Row>
            <span>Auto-Deleveraging Enabled</span>
            <b>
              <TooltipText
                title="This contract is highly speculative and manages loss through auto-deleveraging."
                placement="bottom-start"
              >
                {information?.adlEnabled ? 'Yes' : 'No'}
              </TooltipText>
            </b>
          </Row>
          <Row>
            <span>Mark Method</span>
            <b>{information?.markMethod}</b>
          </Row>
          <Row>
            <span>Open Interest</span>
            <b>{information?.openInterest}</b>
          </Row>
          <Row>
            <span>24H Turnover</span>
            <b>{information?.amount24H}</b>
          </Row>
        </Column>
        <Column>
          <Row>
            <span>Total Volume</span>
            <b>{information?.volume24H}</b>
          </Row>
          <Row>
            <span>Type</span>
            <b>{information?.type}</b>
          </Row>
          <Row>
            <span>Contract Size</span>
            <b>
              <TooltipText
                title="Currently {contractSize} {information?.baseCurrency} per
          contract."
                placement="bottom-end"
              >
                1 USD
              </TooltipText>
            </b>
          </Row>
          <Row>
            <span>Commission</span>
            <b>
              See the{' '}
              <Link to={COMMUNITY_LINKS.DOCS_TRADING_FEES} target="_blank">
                Fees Reference
              </Link>{' '}
              for more details.
            </b>
          </Row>
          <Row>
            <span>Taker Fee Rate</span>
            <b>-</b>
          </Row>
          <Row>
            <span>Maker Fee Rate</span>
            <b>-</b>
          </Row>
        </Column>
      </Body>
    </Wrap>
  )
})
